// frontend/src/pages/dashboard/TeamDashboard.js
// The team-facing overview: admin, dept_head and manager. Employees get
// MyDashboard instead — see pages/Dashboard.js.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getUsers, getOKRs, getCheckins, getDashboardStats,
  getFeedback, getReviews, getReviewSessions
} from '../../services/api';
import {
  MetricCard, Card, Avatar, Badge, ProgressBar, SectionTitle, Spinner, Btn
} from '../../components/UI';
import PageHeader from '../../components/PageHeader';
import { moduleByKey } from '../../content/modules';

const statusColor = s => ({ on_track: 'green', at_risk: 'amber', complete: 'blue', not_started: 'gray' }[s] || 'gray');
const statusLabel = s => ({ on_track: 'On track', at_risk: 'At risk', complete: 'Complete', not_started: 'Not started' }[s] || s);
const checkinStatusColor = s => ({ scheduled: 'blue', complete: 'green', overdue: 'red', cancelled: 'gray' }[s] || 'gray');
const feedbackTypeColor = s => ({ peer: 'blue', manager: 'green', upward: 'amber', self: 'gray' }[s] || 'gray');

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}
function formatDateTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) +
    ' ' + dt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function timeAgo(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export default function TeamDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [okrs, setOkrs] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [stats, setStats] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getUsers().catch(() => []),
      getOKRs().catch(() => []),
      getCheckins().catch(() => []),
      getDashboardStats().catch(() => null),
      getFeedback().catch(() => []),
      getReviews().catch(() => []),
      getReviewSessions().catch(() => []),
    ])
      .then(([u, o, c, s, f, r, rs]) => {
        setUsers(Array.isArray(u) ? u : []);
        setOkrs(Array.isArray(o) ? o : []);
        setCheckins(Array.isArray(c) ? c : []);
        setStats(s);
        setFeedback(Array.isArray(f) ? f : []);
        setReviews(Array.isArray(r) ? r : []);
        setSessions(Array.isArray(rs) ? rs : []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  const overdue = checkins.filter(c => c.status === 'overdue').length;
  const upcomingCheckins = checkins
    .filter(c => c.status === 'scheduled')
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const upcomingSessions = sessions
    .filter(s => s.status === 'scheduled')
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const completedCheckins = checkins
    .filter(c => c.status === 'complete')
    .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
  const completedSessions = sessions
    .filter(s => s.status === 'complete')
    .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
  const recentFeedback = [...feedback].sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)).slice(0, 4);
  const recentReviews = [...reviews].filter(r => r.status === 'submitted').sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)).slice(0, 4);

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <PageHeader moduleKey="home"
        subtitle={`${user?.department_name || user?.program_name || 'Standard PR Program'} · ${user?.name}`} />

      {/* ── Metrics row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10, marginBottom: 28 }}>
        <MetricCard label="Team members" value={stats?.team_size ?? users.length} onClick={() => navigate('/org')} />
        <MetricCard label="Avg rating" value={stats?.avg_rating ?? '--'} change="vs last cycle" changeType="up" onClick={() => navigate('/reviews')} />
        <MetricCard label="OKR progress" value={stats?.avg_okr_progress != null ? `${stats.avg_okr_progress}%` : '--'} change="this quarter" changeType="up" onClick={() => navigate('/okrs')} />
        <MetricCard label="Overdue 1:1s" value={overdue} changeType={overdue > 0 ? 'down' : 'up'} change={overdue > 0 ? 'need attention' : 'all on track'} onClick={() => navigate('/checkins')} />
      </div>

      {/* ── Main grid: left + right columns ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20 }}>

        {/* ── LEFT COLUMN ── */}
        <div>

          {/* OKR Snapshot */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <SectionTitle style={{ margin: 0 }}>OKR snapshot</SectionTitle>
              <Btn variant="ghost" size="sm" onClick={() => navigate('/okrs')}>View all &rarr;</Btn>
            </div>
            {okrs.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10 }}>
                {okrs.slice(0, 6).map(o => (
                  <Card key={o.id} style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.4, flex: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {o.title}
                      </div>
                      <Badge color={statusColor(o.status)}>{statusLabel(o.status)}</Badge>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ProgressBar value={o.progress} color={o.status === 'at_risk' ? 'var(--amber-500)' : 'var(--navy-500)'} />
                      <span style={{ fontSize: 12, fontWeight: 500, width: 32, textAlign: 'right', flexShrink: 0 }}>{o.progress}%</span>
                    </div>
                    {o.owner_name && (
                      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>{o.owner_name}</div>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <Card style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No OKRs for this cycle yet.</Card>
            )}
          </div>

          {/* Feedback Snapshot */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <SectionTitle style={{ margin: 0 }}>Recent feedback</SectionTitle>
              <Btn variant="ghost" size="sm" onClick={() => navigate('/feedback')}>View all &rarr;</Btn>
            </div>
            {recentFeedback.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {recentFeedback.map(f => (
                  <Card key={f.id} style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <Avatar initials={f.from_initials || '?'} size={30} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 500 }}>{f.from_name || 'Anonymous'}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>&rarr;</span>
                          <span style={{ fontSize: 12, fontWeight: 500 }}>{f.to_name}</span>
                          <Badge color={feedbackTypeColor(f.feedback_type)}>{f.feedback_type}</Badge>
                        </div>
                        {f.quote && (
                          <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, fontStyle: 'italic', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            "{f.quote}"
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: 'var(--text-3)' }}>
                          {f.overall_stars && <span>Rating: {f.overall_stars}/5</span>}
                          <span>{timeAgo(f.submitted_at)}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No feedback submitted yet.</Card>
            )}
          </div>

          {/* Recent Reviews Snapshot */}
          {recentReviews.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <SectionTitle style={{ margin: 0 }}>Recent reviews</SectionTitle>
                <Btn variant="ghost" size="sm" onClick={() => navigate('/reviews')}>View all &rarr;</Btn>
              </div>
              <Card style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)' }}>
                      {['Employee', 'Type', 'Score', 'Calibration', 'Submitted'].map(h => (
                        <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentReviews.map(r => (
                      <tr key={r.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar initials={r.reviewee_initials} size={26} />
                            <div>
                              <div style={{ fontWeight: 500, fontSize: 12 }}>{r.reviewee_name}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-3)' }}>by {r.reviewer_name}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px' }}><Badge color="blue">{r.review_type}</Badge></td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ fontWeight: 500, fontSize: 15 }}>{r.overall_score}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>/5</span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <Badge color={r.calibration === 'outstanding' || r.calibration === 'exceeds' ? 'green' : r.calibration === 'meets' ? 'amber' : 'gray'}>
                            {r.calibration || '--'}
                          </Badge>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 11, color: 'var(--text-3)' }}>
                          {formatDate(r.submitted_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          )}

          {/* Team Members Compact */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <SectionTitle style={{ margin: 0 }}>Team members</SectionTitle>
              <Btn variant="ghost" size="sm" onClick={() => navigate('/org')}>View full list &rarr;</Btn>
            </div>
            <Card style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {users.filter(u => u.id !== user?.id).slice(0, 12).map(m => (
                  <Link key={m.id} to={`/profile/${m.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 6px 6px',
                      borderRadius: 'var(--radius-md)', background: 'var(--gray-50)',
                      transition: 'background 0.12s', cursor: 'pointer',
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-100)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--gray-50)'}
                    >
                      <Avatar initials={m.avatar_initials} size={26} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{m.job_title}</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              {users.filter(u => u.id !== user?.id).length > 12 && (
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                  <Btn variant="ghost" size="sm" onClick={() => navigate('/org')}>
                    +{users.filter(u => u.id !== user?.id).length - 12} more
                  </Btn>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div>

          {/* Upcoming Reviews & 1:1 Meetings */}
          <div style={{ marginBottom: 24 }}>
            <SectionTitle>Upcoming meetings & reviews</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Review sessions */}
              {upcomingSessions.slice(0, 3).map(s => (
                <Card key={`rs-${s.id}`} style={{ padding: '12px 14px', borderLeft: '3px solid var(--navy-500)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <Avatar initials={s.reviewee_initials} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{s.reviewee_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)' }}>Review session{s.cycle_name ? ` - ${s.cycle_name}` : ''}</div>
                    </div>
                    <Badge color="green">review</Badge>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: 38 }}>
                    <div>{formatDateTime(s.scheduled_at)}</div>
                    {s.location && <div style={{ color: 'var(--text-3)' }}>{s.location}</div>}
                    {s.agenda && <div style={{ color: 'var(--text-3)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.agenda}</div>}
                  </div>
                </Card>
              ))}

              {/* 1:1 check-ins */}
              {upcomingCheckins.slice(0, 4).map(c => (
                <Card key={`ci-${c.id}`} style={{ padding: '12px 14px', borderLeft: '3px solid var(--blue-500)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <Avatar initials={c.emp_initials || c.employee_initials} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{c.employee_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)' }}>1:1 check-in</div>
                    </div>
                    <Badge color={checkinStatusColor(c.status)}>{c.status}</Badge>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', paddingLeft: 38 }}>
                    {formatDateTime(c.scheduled_at)}
                  </div>
                </Card>
              ))}

              {/* Overdue */}
              {checkins.filter(c => c.status === 'overdue').slice(0, 2).map(c => (
                <Card key={`od-${c.id}`} style={{ padding: '12px 14px', borderLeft: '3px solid var(--red-500)', background: 'var(--red-50)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <Avatar initials={c.emp_initials || c.employee_initials} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{c.employee_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--red-700)' }}>1:1 check-in - overdue</div>
                    </div>
                    <Badge color="red">overdue</Badge>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--red-700)', paddingLeft: 38 }}>
                    Was due {formatDate(c.scheduled_at)}
                  </div>
                </Card>
              ))}

              {upcomingSessions.length === 0 && upcomingCheckins.length === 0 && (
                <Card style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                  No upcoming meetings scheduled.
                </Card>
              )}

              <Btn variant="secondary" size="sm" onClick={() => navigate('/checkins')} style={{ alignSelf: 'flex-start', marginTop: 4 }}>
                View all meetings &rarr;
              </Btn>
            </div>
          </div>

          {/* Last Completed Meetings / Action Items */}
          <div style={{ marginBottom: 24 }}>
            <SectionTitle>Recent completed meetings</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {completedCheckins.slice(0, 3).map(c => (
                <Card key={`cc-${c.id}`} style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <Avatar initials={c.emp_initials || c.employee_initials} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{c.employee_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)' }}>1:1 &middot; {formatDate(c.scheduled_at)}</div>
                    </div>
                    <Badge color="green">complete</Badge>
                  </div>
                  {c.action_items && (
                    <div style={{ paddingLeft: 38 }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', fontWeight: 500, marginBottom: 3 }}>Action items</div>
                      <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                        {c.action_items}
                      </div>
                    </div>
                  )}
                  {c.manager_notes && !c.action_items && (
                    <div style={{ paddingLeft: 38 }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', fontWeight: 500, marginBottom: 3 }}>Notes</div>
                      <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, fontStyle: 'italic', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {c.manager_notes}
                      </div>
                    </div>
                  )}
                </Card>
              ))}

              {completedSessions.slice(0, 2).map(s => (
                <Card key={`cs-${s.id}`} style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <Avatar initials={s.reviewee_initials} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{s.reviewee_name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)' }}>Review session &middot; {formatDate(s.scheduled_at)}</div>
                    </div>
                    <Badge color="green">complete</Badge>
                  </div>
                  {s.notes && (
                    <div style={{ paddingLeft: 38 }}>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', fontWeight: 500, marginBottom: 3 }}>Notes</div>
                      <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, fontStyle: 'italic', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {s.notes}
                      </div>
                    </div>
                  )}
                </Card>
              ))}

              {completedCheckins.length === 0 && completedSessions.length === 0 && (
                <Card style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                  No completed meetings yet.
                </Card>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <SectionTitle>Quick links</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                ['reviews',     'var(--navy-600)'],
                ['kpi-scoring', 'var(--blue-700)'],
                ['feedback',    'var(--amber-700)'],
                ['program-guide-tech', 'var(--navy-600)'],
              ].map(([key, color]) => {
                const mod = moduleByKey(key);
                return { label: mod.label, to: mod.route, color };
              }).map(link => (
                <Link key={link.to} to={link.to} style={{ textDecoration: 'none' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', borderRadius: 'var(--radius-md)',
                    background: 'var(--gray-50)', transition: 'background 0.12s', cursor: 'pointer',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-100)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--gray-50)'}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500, color: link.color }}>{link.label}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>&rarr;</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
